import React from 'react';
import './GlobalHeader.css';
import { resolveHeroImage, type HeroImageConfig } from '@/utils/surveyHero';

/**
 * The brand hero image the FDS `GlobalNav`-style header sits translucent
 * over (SA-1681 fix-09) — a plain decorative image box, `margin-top:-56px`
 * so the sticky header overlays its top 56px. Heights 300px (<768px) /
 * 440px (>=768px); Ford repositions the SAME photo per size (50%/50%
 * mobile, 50%/45% desktop), Lincoln swaps to a distinct crop per size —
 * both differences are expressed as CSS custom properties
 * (`--hero-mobile`/`--hero-desktop`) so a single stylesheet media query
 * (`GlobalHeader.css`) picks the right one; a React inline `style` object
 * can't itself hold a media query.
 *
 * Pure/dumb: takes the already-resolved config and renders nothing when
 * `resolveHeroImage()` returns null (`heroImage: false`, or
 * `isFordProHeader`) — never renders a translucent header with nothing
 * behind it.
 */

interface SurveyHeroProps {
  brand: 'Ford' | 'Lincoln';
  heroImage?: HeroImageConfig;
  isFordProHeader?: boolean;
  /**
   * The header above is solid (never translucent), so the hero starts
   * BELOW it instead of tucking its top 56px underneath — Ford's case,
   * matching ford.com's solid white header.
   */
  flush?: boolean;
}

const SurveyHero: React.FC<SurveyHeroProps> = ({ brand, heroImage, isFordProHeader, flush = false }) => {
  const resolved = resolveHeroImage({ heroImage, brand, isFordProHeader });
  if (!resolved) return null;

  const style = {
    '--hero-mobile': `url("${resolved.mobile}")`,
    '--hero-desktop': `url("${resolved.desktop}")`,
  } as React.CSSProperties;

  return (
    <div
      className={['survey-hero', brand === 'Ford' ? 'survey-hero--ford' : '', flush ? 'survey-hero--flush' : '']
        .filter(Boolean)
        .join(' ')}
      style={style}
      aria-hidden="true"
    />
  );
};

export default SurveyHero;
